import * as THREE from 'three';

const C = {
  night: 0x111720,
  haze: 0x6f7f86,
  basalt: 0x282434,
  basaltLight: 0x40394b,
  bone: 0xcfc2a5,
  chalk: 0xa99f8b,
  teal: 0x2f6f6b,
  ember: 0xf08a5b,
  citrine: 0xd7b94c,
};

export function createPreviewScene(mount: HTMLElement): () => void {
  if (!document.createElement('canvas').getContext('webgl2')) {
    mount.textContent = 'WebGL2 is unavailable in this browser.';
    mount.dataset.webgl = 'unavailable';
    return () => undefined;
  }

  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance', preserveDrawingBuffer: true });
  renderer.setPixelRatio(1);
  renderer.setSize(960, 530, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.domElement.setAttribute('aria-label', 'Cinderwell Span environment concept keyframe');
  mount.replaceChildren(renderer.domElement);
  mount.dataset.webgl = 'webgl2';

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(C.night);
  scene.fog = new THREE.FogExp2(0x26313a, 0.028);
  const camera = new THREE.PerspectiveCamera(43, 960 / 530, 0.1, 120);
  camera.position.set(-10.5, 5.1, 17.5);
  camera.lookAt(2.2, 4.6, -8);

  const geometries: THREE.BufferGeometry[] = [];
  const materials: THREE.Material[] = [];
  const objects: THREE.Object3D[] = [];
  const mat = (color: number, roughness = 0.9, metalness = 0) => {
    const value = new THREE.MeshStandardMaterial({ color, roughness, metalness });
    materials.push(value);
    return value;
  };
  const addMesh = (geometry: THREE.BufferGeometry, material: THREE.Material, position: [number, number, number], rotation: [number, number, number] = [0, 0, 0]) => {
    geometries.push(geometry);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(...position);
    mesh.rotation.set(...rotation);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    scene.add(mesh);
    objects.push(mesh);
    return mesh;
  };

  const basalt = mat(C.basalt, 0.96);
  const basaltLight = mat(C.basaltLight, 0.92);
  const bone = mat(C.bone, 0.91);
  const chalk = mat(C.chalk, 1);
  const teal = mat(C.teal, 0.72, 0.08);
  const copper = mat(0x8f4937, 0.5, 0.4);
  const ember = new THREE.MeshStandardMaterial({ color: C.ember, emissive: 0x8c2815, emissiveIntensity: 2.4, roughness: 0.5 }); materials.push(ember);

  // Basin floor, stepped chalk shelves, and distant haze silhouettes.
  addMesh(new THREE.PlaneGeometry(90, 90), chalk, [0, -0.3, -12], [-Math.PI / 2, 0, 0]);
  for (let i = 0; i < 7; i += 1) {
    const width = 13 + i * 2.2;
    addMesh(new THREE.BoxGeometry(width, 0.45, 3.2), i % 2 ? chalk : bone, [-12 + i * 4.8, 0.05 + i * 0.1, -11 - i * 3.8], [0, -0.12 + i * 0.035, 0]);
  }
  for (let i = 0; i < 10; i += 1) {
    addMesh(new THREE.ConeGeometry(2.2 + (i % 3), 15 + (i % 4) * 2.8, 4), i % 2 ? basalt : basaltLight, [-25 + i * 6.2, 7, -27 - (i % 3) * 5], [0, i * 0.21, -0.18 + (i % 2) * 0.28]);
  }

  // The causeway recedes toward the intake tower.
  addMesh(new THREE.BoxGeometry(5.8, 0.72, 30), bone, [1.8, 2.1, -6.8], [0, -0.13, 0.015]);
  addMesh(new THREE.BoxGeometry(0.34, 1.25, 30), copper, [-1.0, 2.15, -6.8], [0, -0.13, 0]);
  addMesh(new THREE.BoxGeometry(0.22, 0.18, 29), ember, [0.25, 2.55, -6.8], [0, -0.13, 0]);
  for (let z = 6; z > -19; z -= 3.3) {
    addMesh(new THREE.BoxGeometry(5.7, 0.12, 0.16), basaltLight, [1.8 + (z - 6) * -0.13, 2.5, z], [0, -0.13, 0]);
  }

  // Leaning fins frame the route.
  [-7.1, -3.8, 7.6, 11.2].forEach((x, i) => {
    addMesh(new THREE.ConeGeometry(1.1 + i * 0.12, 11 + i * 1.2, 4), basalt, [x, 5.1, -2.5 - i * 4.6], [0, 0.35 + i * 0.17, i % 2 ? 0.16 : -0.22]);
  });

  // Tilted intake tower: stacked ceramic shell, copper ribs, teal crown.
  const tower = addMesh(new THREE.CylinderGeometry(3.1, 4.2, 12.5, 7), basalt, [5.0, 7.2, -18.3], [0.02, 0.28, -0.10]);
  for (let y = 2.5; y < 12; y += 2.15) {
    addMesh(new THREE.TorusGeometry(3.15 - y * 0.035, 0.11, 6, 7), copper, [5.0 + y * 0.025, y + 1.2, -18.3], [Math.PI / 2, 0, 0.28]);
  }
  addMesh(new THREE.TorusGeometry(3.0, 0.38, 8, 7), teal, [5.45, 13.0, -18.3], [Math.PI / 2, 0, 0.28]);
  addMesh(new THREE.ConeGeometry(2.0, 3.0, 7, 1, true), bone, [5.45, 14.3, -18.3], [0, 0.28, -0.10]);
  tower.userData.hero = true;

  // Suspended ceramic wind cups and fine copper support lines.
  const lineMaterial = new THREE.LineBasicMaterial({ color: 0x9b6752, transparent: true, opacity: 0.72 }); materials.push(lineMaterial);
  const anchorA = new THREE.Vector3(-6.5, 12.2, -5.5);
  const anchorB = new THREE.Vector3(5.4, 15.1, -18.3);
  const lineGeometry = new THREE.BufferGeometry().setFromPoints([anchorA, anchorB]); geometries.push(lineGeometry);
  const line = new THREE.Line(lineGeometry, lineMaterial); scene.add(line); objects.push(line);
  for (let i = 0; i < 6; i += 1) {
    const t = (i + 0.65) / 6.7;
    const p = anchorA.clone().lerp(anchorB, t);
    p.y -= Math.sin(t * Math.PI) * 1.4;
    addMesh(new THREE.CylinderGeometry(0.55, 0.9, 1.1, 8, 1, true), bone, [p.x, p.y, p.z], [0.08, i * 0.25, Math.PI]);
    addMesh(new THREE.TorusGeometry(0.55, 0.08, 6, 10), copper, [p.x, p.y + 0.48, p.z], [Math.PI / 2, 0, 0]);
  }

  // Sparse route markers and foreground debris.
  for (let i = 0; i < 8; i += 1) {
    addMesh(new THREE.BoxGeometry(0.18, 1.4 + (i % 3) * 0.35, 0.18), copper, [-2.2 + i * 1.28, 1.0, 5 - i * 2.7], [0, 0, -0.12]);
    addMesh(new THREE.BoxGeometry(0.52, 0.16, 0.52), i % 3 === 0 ? teal : bone, [-2.05 + i * 1.28, 1.65 + (i % 3) * 0.17, 5 - i * 2.7], [0, i * 0.4, 0]);
  }

  scene.add(new THREE.HemisphereLight(0x8fb2c2, 0x241c23, 2.7));
  const sun = new THREE.DirectionalLight(0xffe5bd, 4.3);
  sun.position.set(-12, 20, 8); sun.castShadow = true; sun.shadow.mapSize.set(2048, 2048); scene.add(sun);
  const heat = new THREE.PointLight(C.ember, 38, 21, 1.8); heat.position.set(0.3, 2.9, -4.5); scene.add(heat);
  const rim = new THREE.PointLight(0x4faaa2, 20, 18, 2); rim.position.set(7, 13, -15); scene.add(rim);

  renderer.render(scene, camera);
  return () => {
    objects.forEach((object) => scene.remove(object));
    geometries.forEach((geometry) => geometry.dispose());
    materials.forEach((material) => material.dispose());
    renderer.dispose();
  };
}
